/** Port of the My Bookings list for ONE tour — `TripsScreenStateMapper.mapSections` /
 *  `mapSection`, `CommonUiMapperImpl.mapTour` (style SINGLE → `CardActivityItem.Big`) and
 *  `mapDates` / `Dates.getFormattedStartDate`, `TourDatesMapper.mapDates`, and the `Dates`
 *  predicates (`isUpComing`, `isPast`, `isSpansMultipleDays`).
 *
 *  Statement for statement where it maps; what the list holds besides this tour (other
 *  bookings, the search box, the "+" button, the "Not seeing your booking?" footer) is
 *  not drawn — named in `notes`. A trip hidden from My Bookings gets no section and no
 *  card (`tours.filter { it.myTripsDisplay }`).
 *
 *  Spec §1E. */
import { stripHtml } from '../text/androidHtml';
import type { DomainImage } from '../images';
import type { MyTripsTour } from '../adapters/myTrips';

/** strings_new.xml */
export const STRINGS = {
  title: 'My Bookings', // home_title_my_trips
  active: 'Active', // tour_active
  upcoming: 'Upcoming', // tour_upcoming
  past: 'Where you’ve been', // tour_past (Text.Style.Typeface.Bold)
  dateTime: (date: string, time: string) => `${date} (${time})`, // my_trips_date_time
  dateTimeRange: (a: string, b: string) => `${a} – ${b}`, // my_trips_date_time_range
} as const;

export type MyTripsItem =
  | { kind: 'section'; key: string; text: string; bold: boolean }
  | {
      kind: 'cardBig';
      key: string;
      overline: string;
      title: string;
      description: string | null;
      image: DomainImage | null;
    };

export interface MyTripsUiState {
  title: string;
  items: MyTripsItem[];
  /** The trip is not in the list at all (`myTripsDisplay` off). */
  hidden: boolean;
  notes: string[];
}

/** `Dates` as `TourDatesMapper.mapDates` builds it: end = start + length. */
export interface MyTripsDates {
  start: Date;
  end: Date;
  hasDepartures: boolean;
}

export function mapDatesOf(tour: MyTripsTour, now: Date): MyTripsDates {
  const start = tour.startDate
    ? (() => {
        const [y, m, d] = tour.startDate.split('-').map(Number);
        return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
      })()
    : new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return { start, end: new Date(start.getTime() + tour.lengthSeconds * 1000), hasDepartures: tour.hasDepartures };
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Text.Time.Format.ShortDate — "MMM d". */
export function formatShortDate(d: Date): string {
  return `${MONTH_ABBR[d.getMonth()]} ${d.getDate()}`;
}

/** Text.Time.Format.ShortTime — "h:mm a" (en-US, 12-hour). */
export function formatShortTimeOf(d: Date): string {
  const h = d.getHours();
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:${String(d.getMinutes()).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** `Dates.isSpansMultipleDays` — a different local date at the end. */
function spansMultipleDays(dates: MyTripsDates): boolean {
  const a = new Date(dates.start.getFullYear(), dates.start.getMonth(), dates.start.getDate());
  const b = new Date(dates.end.getFullYear(), dates.end.getMonth(), dates.end.getDate());
  return Math.round((b.getTime() - a.getTime()) / 86_400_000) >= 1;
}

/** `Dates.getFormattedStartDate`. */
function formattedStartDate(dates: MyTripsDates): string {
  return dates.hasDepartures && !spansMultipleDays(dates)
    ? STRINGS.dateTime(formatShortDate(dates.start), formatShortTimeOf(dates.start))
    : formatShortDate(dates.start);
}

/** `CommonUiMapperImpl.mapDates(dates, style = null)` — always uppercased. */
export function mapDates(dates: MyTripsDates): string {
  const sameDay = dates.start.getMonth() === dates.end.getMonth() && dates.start.getDate() === dates.end.getDate();
  const text = sameDay ? formattedStartDate(dates) : STRINGS.dateTimeRange(formattedStartDate(dates), formatShortDate(dates.end));
  return text.toUpperCase();
}

/** The section a tour lands in: `isUpComing` → Upcoming, `isPast` → Where you've been,
 *  else Active. */
export function sectionOf(dates: MyTripsDates, now: Date): 'upcoming' | 'past' | 'active' {
  if (dates.start > now) return 'upcoming';
  if (dates.end < now) return 'past';
  return 'active';
}

export function mapMyTrips(tour: MyTripsTour, now: Date = new Date()): MyTripsUiState {
  const notes = [
    'The list shows this trip only: other bookings, the search box, the "+" button and the ' +
      '"Not seeing your booking?" footer are not drawn.',
    tour.startDate
      ? 'Dates: the fixed-date tour’s start date; the app shows each booking’s own departure time.'
      : 'Dates: an example booking starting today; each booking shows its own dates.',
  ];
  if (!tour.myTripsDisplay) {
    return { title: STRINGS.title, items: [], hidden: true, notes };
  }
  const dates = mapDatesOf(tour, now);
  const section = sectionOf(dates, now);
  const items: MyTripsItem[] = [
    {
      kind: 'section',
      key: `${section}_section`,
      text: STRINGS[section],
      bold: section === 'past',
    },
    {
      kind: 'cardBig',
      key: `tour_${tour.id}`,
      overline: mapDates(dates),
      title: tour.name,
      description: tour.description ? stripHtml(tour.description) : null,
      image: tour.coverImage,
    },
  ];
  return { title: STRINGS.title, items, hidden: false, notes };
}
